import { createContext, useContext, useEffect, useState } from 'react'
import { IntercomProvider, useIntercom } from 'react-use-intercom'
import { useUserContext } from '~/core/context'
import { Toolbox } from '~/plugins/toolbox/shared'
import { Wrapper } from './Wrapper'

const useInternal = () => {
  const { session } = useUserContext()

  const { boot, update, trackEvent, show, hide, shutdown } = useIntercom()

  const clientId = import.meta.env.VITE_INTERCOM_APP_ID
  const isAuthorised = Toolbox.Utility.isDefined(clientId)

  const [isBooted, setBooted] = useState(false)

  useEffect(() => {
    if (!isAuthorised) {
      return
    }

    if (session.user?.id) {
      boot({
        name: session.user.name ?? 'Unknown',
        email: session.user.email ?? 'unknown@marblism.com',
        customAttributes: {
          status: session.user.status,
          subscriptionProductKey: session.user.subscriptionProductKey,
          subscriptionStatus: session.user.subscriptionStatus,
          susbcriptionDateStart: session.user.susbcriptionDateStart,
        },
      })

      setBooted(true)
    } else if (isBooted) {
      shutdown()
      setBooted(false)
    }
  }, [clientId, session.user?.id])

  useEffect(() => {
    if (isBooted) {
      // show()
    }
  }, [isBooted])

  useEffect(() => {
    if (!isBooted) {
      return
    }

    if (session.user?.id) {
      update({
        customAttributes: {
          status: session.user.status,
          subscriptionProductKey: session.user.subscriptionProductKey,
          subscriptionStatus: session.user.subscriptionStatus,
          susbcriptionDateStart: session.user.susbcriptionDateStart,
        },
      })
    }
  }, [clientId, session.user])

  return {
    isAuthorised,
    show,
  }
}

type IntercomContext = ReturnType<typeof useInternal>

const IntercomContext = createContext<IntercomContext>(undefined)

const ProviderActive = ({ children }) => {
  const value = useInternal()

  return (
    <IntercomContext.Provider value={value}>
      <Wrapper>{children}</Wrapper>
    </IntercomContext.Provider>
  )
}

const ProviderMocked = ({ children }) => {
  return (
    <IntercomContext.Provider value={{ isAuthorised: false, show: () => {} }}>
      <Wrapper>{children}</Wrapper>
    </IntercomContext.Provider>
  )
}

export const Provider = ({ children }) => {
  const appId = import.meta.env.VITE_INTERCOM_APP_ID

  if (!appId) {
    return (
      <>
        <ProviderMocked>{children}</ProviderMocked>
      </>
    )
  }

  return (
    <IntercomProvider appId={appId}>
      <ProviderActive>{children}</ProviderActive>
    </IntercomProvider>
  )
}

export const useIntercomContext = (): IntercomContext => {
  const context = useContext(IntercomContext)

  if (context === undefined) {
    throw new Error('useIntercomContext must be used within a IntercomProvider')
  }

  return context
}
